import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import images from "../common/images";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Hero from "../components/Hero";
import FavouriteIcon from "../components/FavouriteIcon";
import EventCard from "../components/EventCard";
import BookArtistForm from "../components/BookArtistForm";
import ImageCarousel from "../components/ImageCarousel";
import insta from "../assets/img/icons8-instagram-logo-48.png";
import youtubeImg from "../assets/img/icons8-youtube-48.png";
import { getArtistPath } from "../utils/clubPath";
import NotFound from "./notFound";
import LoadingSpinner from "../components/LoadingSpinner";
import useMobileStoreRedirect from "../hooks/useMobileStoreRedirect";

const ArtistDetails = () => {
  const navigate = useNavigate();
  const { artistId } = useParams();
  const { data: artist, isError, isFetched, isLoading } = useQuery({
    queryKey: [`api/artists/${artistId}`],
    enabled: Boolean(artistId),
    retry: false,
  });

  const artistObjectId = artist?.data?._id;
  useMobileStoreRedirect("artists", artistObjectId || artistId);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!artist?.data) return;

    const resolvedKey = artist.data.slug || artist.data._id || artistId;
    const onLegacyPath = window.location.pathname.startsWith("/artists/");

    if (resolvedKey && (onLegacyPath || artistId !== resolvedKey)) {
      navigate(getArtistPath(artist.data), { replace: true });
    }
  }, [artist?.data, artistId, navigate]);

  const queryParams = new URLSearchParams();
  queryParams.append("upcoming", 1);
  queryParams.append("page", 0);
  if (artistObjectId) {
    queryParams.append("artistId", artistObjectId);
  }

  const { data: events } = useQuery({
    queryKey: [`api/events?${queryParams}`],
    enabled: Boolean(artistObjectId),
  });

  const token = localStorage.getItem("userToken");
  const isLoggedIn = JSON.parse(token);
  const [isFormOpened, setIsFormOpened] = useState(false);

  const handleLinkClick = (e) => {
    if (!isLoggedIn) {
      e.preventDefault();
      navigate("/signin");
    }
  };

  if (!artistId || isLoading || !isFetched) {
    return (
      <Layout>
        <LoadingSpinner />
      </Layout>
    );
  }

  if (isError || !artist?.data?._id) {
    return <NotFound />;
  }

  return (
    <Layout>
      <Hero title="Artist Details" />
      {/* Details List */}
      <section
        className="details-sec section-gap wow fadeInUp"
        data-wow-delay="0.2s"
      >
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-7">
              <div className="about-club-sec">
                <div className="details-head-sec">
                  <div className="text-sec">
                    <h3 className="title-30">{artist?.data?.name}</h3>
                    <div className="cnt-sec">
                      <p className="p-20">
                        {artist?.data?.type?.toLowerCase() === "artist"
                          ? "SINGER"
                          : artist?.data?.type?.toUpperCase()}
                      </p>
                      <p className="p-20">{artist?.data?.musicGenre}</p>
                    </div>
                  </div>
                  <div className="details-head-sec__actions">
                    <FavouriteIcon
                      endpoint={`api/user/likes/artists/${artist?.data?._id}`}
                      item={artist?.data}
                      isLiked={!artist?.data?.meta?.isLiked}
                    />
                  </div>
                </div>
                <div className="club-direction">
                  {import.meta.env?.VITE_ENABLE_BOOKARTISTFORM ? (
                  <div className="ml-4 mb-4 px-6">
                     <button type="button" onClick={() => setIsFormOpened(prev => !prev)} className="btn btn-dark btn-lg">Book Artist</button>
                  </div>
                  ): null}
                  <BookArtistForm artistId={artist?.data?._id} isOpen={isFormOpened} closeModal={() => setIsFormOpened(false)} />
                  <div className="row no-gutters">
                    <div className="col-md-6 col-lg-6">
                      <div
                        className={`sec radius-left ${
                          artist?.data?.instagramLink ? "active" : ""
                        }`}
                      >
                        <a
                          href={artist?.data?.instagramLink}
                          // onClick={handleLinkClick}
                        >
                          <div className="icon">
                            <img src={insta} className="icon__img" />
                          </div>
                          <h4 className="title-18">Instagram</h4>
                        </a>
                      </div>
                    </div>
                    <div className=" col-md-6 col-lg-6">
                      <div
                        className={`sec radius-right ${
                          artist?.data?.youtubeChannelLink ? "active" : null
                        }`}
                      >
                        <a
                          href={artist?.data?.youtubeChannelLink || ""}
                          onClick={handleLinkClick}
                        >
                          <div className="icon">
                            <img src={youtubeImg} className="icon__img" />
                          </div>
                          <h4 className="title-18">Youtube</h4>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-5">
              <div className="details-slider-sec">
                <div id="slider-items" className="itmes-slider no-space">
                  <ImageCarousel
                    images={Array.isArray(artist?.data?.images) && artist?.data?.images?.length
                      ? artist?.data?.images
                      : [images.background]
                    }
                    autoplay={true}
                    loop={true}
                  />
                  <div className="owl-theme">
                    <div className="owl-controls">
                      <div className="custom-nav owl-nav"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Details List End */}

      {/* Card List */}
      <section className="card-heading-sec section-gap">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="title-sec wow fadeInUp" data-wow-delay="0.3s">
                <h2 className="title-25">
                  {
                    // haveTodaysEvent()?.length
                    //   ? "Whats Happening Today"
                    //   :
                    "Upcoming Events"
                  }
                </h2>
              </div>
            </div>
          </div>
          <div className="card-list">
            <div className="row">
              {
                // (haveTodaysEvent()?.length
                //   ? haveTodaysEvent()
                //   : events?.data?.slice(0, 1) || []
                // )
                events?.data?.length ? (
                  events?.data?.map((event, index) => (
                    <EventCard className="col-lg-6 col-xl-3 wow fadeInUp" event={event} key={index} index={index} isCard={true} />
                  ))
                ) : (
                  <div className="no-data">
                    <p className="no-data-found">No events found</p>
                  </div>
                )
              }
            </div>
          </div>
        </div>
      </section>
      {/* Card List End */}

      <a href="#" className="back-to-top">
        <i className="fa fa-chevron-up"></i>
      </a>
    </Layout>
  );
};

export default ArtistDetails;
